import {formatBookingTimestamp, getBrowserTimezone} from "../../util/DateUtils";
import React, {useEffect, useMemo, useState} from "react";
import {Button, CircularProgress, Grid, IconButton, Menu, MenuItem, SxProps, Tooltip, Typography} from "@mui/material";
import {MoreHoriz} from '@mui/icons-material';
import {IActionItem, IData} from "../deprecatedUikit/InfoCard/InfoCard";
import {
  BookingCardCardContainer,
  BookingCardCardHeader,
  BookingCardCardHeaderTitle,
  BookingCardMarginTop,
  BookingCardStatusLabel
} from "./styles";
import {bookingStatusLabel, bookingSubscriptionStatusLabel} from "./booking-card-constants";
import BookingCardRow from "./BookingCardRow";
import ServiceTypes from "../ServiceTypes";
import {SeverityColor} from "../constants/securspace-constants";

export interface IDataValue {
  value: string | number | boolean;
  label: string;
  id?: string;
  cols?: number;
  onClick?: (data: IData) => void;
}

type ButtonVariant = 'text' | 'outlined' | 'contained';

interface IButton {
  label: string;
  onClick: (data: any) => void;
  variant: ButtonVariant;
  styles?: SxProps
  tooltip?: string;
  disabled?: boolean;
  /** (optional) show a spinner and disable the button while its action is in progress */
  loading?: boolean;
}

export interface ICardType {
  /** A single object with all data provided to the component*/
  data: IData;
  /** The cell titles & formatted values to be displayed by the card*/
  headCells: Array<{ id: string; label: string; cols?: number; onClick?: (data: IData) => void }>;
  /** (optional) styles to override the value display*/
  rootStyle?: string;
  /** (optional) styles to override the header title display*/
  headerStyle?: string;
  /** (optional) set the type of user who is viewing the cards. Used to conditionally render buttons and actions. (Buyer by default) */
  bookingType?: 'buyer' | 'supplier';
  /** (optional) overrides the company name display as the title of the card */
  bookingTitle?: string;
  /** (optional) conditionally renders hot dog menu (default is false) */
  hasAction?: boolean;
  /** (optional) conditionally renders the colorful status display under card title (default is true) */
  isStatusShow?: boolean;
  /** (optional) a list of items to be rendered by the card action button */
  actionItems?: IActionItem[];
  /** (optional) a list of buttons that render at the top of the card*/
  buttons?: IButton[];
}

const transformCell = (data: IData, headCell: { id: string; label: string; cols?: number; onClick?: (data: IData) => void }) => {
  const transformedData: IDataValue = {
    value: data[headCell?.id] || '',
    label: headCell?.label || '',
    id: headCell?.id,
    cols: headCell?.cols || 1,
    onClick: headCell?.onClick || undefined
  };
  return transformedData;
};

/**
 * A component for displaying booking information in a card format.
 * @param data {IData} A single object with all data provided to the component
 * @param headCells {Array<{ id: string; label: string, cols?: number }>} The cell titles & formatted values to be displayed by the card
 * @param rootStyle {string} (optional) styles to override the value display
 * @param headerStyle {string} (optional) styles to override the header title display
 * @param bookingType {'buyer' | 'supplier'} (optional) set the type of user who is viewing the cards. Used to conditionally render buttons and actions. (Buyer by default)
 * @param bookingTitle {string} (optional) overrides the company name display as the title of the card
 * @param hasAction {boolean} (optional) conditionally renders hot dog menu (default is false)
 * @param isStatusShow {boolean} (optional) conditionally renders the colorful status display under card title (default is true)
 * @param actionItems {IActionItem[]} (optional) a list of items to be rendered by the card action button
 * @param buttons {IButton[]} (optional) a list of buttons that render at the top of the card
 * @deprecated
 */
const BookingCard = ({
                       data,
                       rootStyle,
                       headerStyle,
                       bookingType = 'buyer',
                       hasAction,
                       actionItems,
                       headCells,
                       isStatusShow = true,
                       buttons,
                       bookingTitle
                     }: ICardType) => {
  const [statusLabel, setStatusLabel] = useState<{label: string; severity: SeverityColor}>({label: "", severity: 'error'});
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );

  const timezone = getBrowserTimezone();
  const transformedData = useMemo<Array<IDataValue>>(() => {
    return headCells.map(cell => cell.id === "creationDate"
      ? {...transformCell(data, cell), value: formatBookingTimestamp(data.creationDate, timezone)}
      : transformCell(data, cell));
  }, [data, headCells, timezone]);

  useEffect(() => {
    let label: string | number | boolean = "";
    let className: SeverityColor = 'error';
    const {bookingSubscriptionStatus, status, frequency, active, assetType} = data;

    // Service/fee asset types (overage spaces, gate fees, lift fees, etc.) are system-generated
    // billing artifacts, not buyer-managed reservations. They still surface status badges such as
    // PENDING/INCOMPLETE while awaiting completion, but never an ACTIVE/INACTIVE badge.
    const isServiceTypeBooking = typeof assetType === 'string' && ServiceTypes.OPTIONS.includes(assetType);

    if (status === bookingStatusLabel.PAYMENT_DECLINED) {
      label = "INACTIVE";
      className = 'error';
    } else if (status === bookingStatusLabel.PENDING) {
      label = "PENDING";
      className = 'warning';
    } else if (status === bookingStatusLabel.INCOMPLETE) {
      label = "INCOMPLETE";
    } else if (frequency === "RECURRING" && bookingSubscriptionStatus && bookingSubscriptionStatus !== bookingSubscriptionStatusLabel.NONE) {
      label = bookingSubscriptionStatus;
      if (status === bookingStatusLabel.APPROVED && bookingSubscriptionStatus === bookingSubscriptionStatusLabel.CANCELLED) {
        label = "SUBSCRIPTION CANCELLED"
      } else if (bookingSubscriptionStatus === bookingSubscriptionStatusLabel.ACTIVE) {
        if (!isServiceTypeBooking && bookingType === 'buyer' && active === false) {
          // A recurring booking's bookingSubscriptionStatus is never flipped when it lapses (there is
          // no batch job that expires subscriptions), so it can still read ACTIVE after the end date
          // has passed. Trust the backend-computed `active` flag instead — it is timezone-aware and
          // already factors in the end date and status — so an expired booking shows INACTIVE rather
          // than a green ACTIVE badge while it sits on the Inactive tab.
          label = "INACTIVE";
          className = 'error';
        } else {
          className = 'success';
        }
      }
    } else if (status === bookingStatusLabel.CANCELLED) {
      label = "CANCELLED"
    } else if (
      !isServiceTypeBooking && (bookingType === 'buyer') && (status === bookingStatusLabel.DECLINED ||
        active === false)
    ) {
      label = "INACTIVE";
    } else if (!isServiceTypeBooking && (bookingType === 'buyer') &&
      (status === bookingStatusLabel["PROCESSING-ACH-REFUND-ADJUSTED"] ||
        status === bookingStatusLabel["PROCESSING-ACH-PAYMENT"] ||
        status === bookingStatusLabel["Approved-Adjusted"] ||
        status === bookingStatusLabel.APPROVED)) {
      label = "ACTIVE";
      className = 'success';
    } else {
      if (!isServiceTypeBooking && bookingType === 'supplier') {
        label = active === 'Active' ? 'ACTIVE' : 'NOT ACTIVE';
        className = active === 'Active' ? 'success' : 'error';
      }
    }
    setStatusLabel({label: String(label), severity: className});
  }, [data, bookingType]);


  const handleOpen = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    if (event.currentTarget instanceof HTMLButtonElement) {
      setMenuAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setMenuAnchorEl(null);
  };

  return (
    <BookingCardCardContainer item mb={'1rem'} className={rootStyle}>
      <BookingCardCardHeader
        className={headerStyle}
        container
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Grid item>
          <BookingCardCardHeaderTitle variant="h6">
            {
              bookingTitle ? bookingTitle : data.locationName && data.supplierCompanyName ? `${data?.locationName} - ${data?.supplierCompanyName}` : `${data?.buyerCompanyName}`
            }
          </BookingCardCardHeaderTitle>
          {
            isStatusShow && statusLabel.label && <BookingCardStatusLabel item severity={statusLabel.severity}>
              <Typography variant="body2">
                {statusLabel.label}
              </Typography>
            </BookingCardStatusLabel>
          }
        </Grid>
        <Grid item sx={{
          display: 'flex',
          gap: '0.5em',
          flexWrap: 'wrap'
        }}>
          {
            buttons && buttons.map((button) => {
              const buttonElement = (
                <Button
                  onClick={() => button.onClick(data)}
                  variant={button.variant || "outlined"}
                  sx={button.styles}
                  disabled={!!button.disabled || !!button.loading}
                  aria-busy={button.loading || undefined}
                  startIcon={button.loading ? <CircularProgress size={14} color="inherit" aria-hidden="true"/> : undefined}
                >
                  {button.label}
                </Button>
              );

              // Only wrap in a Tooltip when a tooltip string is provided.
              // Passing an empty string would still mount the Tooltip with an
              // empty title, adding an unnecessary wrapper.
              return button.tooltip ? (
                <Tooltip title={button.tooltip} key={button.label}>
                  <span>{buttonElement}</span>
                </Tooltip>
              ) : (
                <React.Fragment key={button.label}>{buttonElement}</React.Fragment>
              );
            })
          }
        </Grid>
      </BookingCardCardHeader>
      <BookingCardMarginTop
        spacing={2}
        container
        alignItems="center"
      >
        {Object.entries(transformedData).map(([key, payload]) => (
          <BookingCardRow
            key={key}
            label={payload?.label}
            value={payload?.value?.toString() || "N/A"}
            listId={payload?.id?.toString() || ''}
            data={data}
            cols={payload?.cols || 1}
          />
        ))}
      </BookingCardMarginTop>
      <Grid item sx={{
        position: 'absolute',
        right: "3em",
        top: "25%",
        padding: "0em",
        '@media (max-width: 600px)': {
          top: "10%",
        }
      }}>
        {
          hasAction && <IconButton onClick={handleOpen}>
            <MoreHoriz fontSize="medium" color="primary"/>
          </IconButton>
        }
        {hasAction && actionItems && actionItems?.length > 0 && (
          <Menu
            id="simple-menu"
            anchorEl={menuAnchorEl}
            keepMounted
            open={Boolean(menuAnchorEl)}
            onClose={handleClose}
            sx={{
              '& .MuiPopover-paper': {
                boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
              }
            }}
          >
            {actionItems?.map((item, index) => (
              <MenuItem
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                  item.onClick(data as any);
                }}
              >
                {item.icon}
                <Typography
                  sx={{ml: 2}}
                  variant="body1"
                  color="primary"
                >
                  {item.label}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        )}
      </Grid>
    </BookingCardCardContainer>
  );
};

export default BookingCard;
